import { Edit, Delete } from '@mui/icons-material';
import { Grid, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { DataGrid, GridColDef, GridSortModel, gridClasses } from '@mui/x-data-grid';
import { isEmpty } from 'lodash-es';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ITemplateDTO,
  RtrTemplateDTO,
  getAllRtrTemplate,
  deleteAllRtrTemplate,
} from 'wisestep-api';
import {
  getDateUsingFormat,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsDrawer,
  WsTypoVariants,
} from 'wisestep-base';

import ConfirmDeleteDialog from '../../components/confirmDeleteDialog/ConfirmDeleteDialog';
import { SettingsPageLayout } from '../../components/SettingsPageLayout';
import CreateTemplateEditor from '../emailSettings/components/templates/CreateTemplateEditor';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../teams/helper/defaultValues';

const useStyles = makeStyles({
  customButton: {
    textTransform: 'none',
    padding: '12px 24px',
    maxWidth: '300px',
    whiteSpace: 'nowrap',
    lineHeight: '1.4',
  },
});
export interface CreateProps {
  openDialog?: boolean;
  onEditOrCloneTemplate?: ITemplateDTO;
  onCloneTemplate?: ITemplateDTO;
  variant?: 'new' | 'edit' | 'clone';
  templateType?: string;
}

const RTRTemplate: FC<CreateProps> = ({ onEditOrCloneTemplate, variant, templateType }) => {
  const classes = useStyles();

  const methods = useForm();
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'templateName', sort: 'asc' },
  ]);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [isEditRtrTemplate, setIsEditRtrTemplate] = useState(false);
  const [rtrLisrtData, setRtrListData] = useState<RtrTemplateDTO[]>([]);
  const [rtrLisrtRecord, setRtrListRecord] = useState<RtrTemplateDTO>();

  const [selectedItem, setSelectedItem] = useState<RtrTemplateDTO | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const getAllRtrTemplateList = useCallback(async () => {
    try {
      const sortBy = !isEmpty(sortModel) ? sortModel[0].field : undefined;
      const sortOrder = !isEmpty(sortModel) ? sortModel[0].sort : undefined;
      const response = await getAllRtrTemplate(pageNo, pageSize, '', sortBy, sortOrder);
      setTotalRecord(response.data.totalRecord);
      setRtrListData(response.data.content);
    } catch (error) {
      console.error('Error fetching information categories:', error);
    }
  }, [sortModel, pageNo, pageSize]);

  useEffect(() => {
    getAllRtrTemplateList();
    setIsEditRtrTemplate(false);
  }, [getAllRtrTemplateList]);

  const closeDialog = () => {
    setShowCreateTemplate(false);
    getAllRtrTemplateList();
  };
  const tableData: Record<string, unknown>[] = [];
  if (rtrLisrtData) {
    rtrLisrtData.forEach((rtrListItem, index) => {
      tableData.push({
        id: rtrListItem.id,
        templateName: rtrListItem.templateName,
        createdBy: rtrListItem.createdBy,
        createdOn: rtrListItem.createdOn,
        index: index,
      });
    });
  }

  const columns: GridColDef[] = [
    {
      field: 'templateName',
      headerName: 'Template Name',
      flex: 0.5,
      renderCell: (params) => (
        <WsTypography
          variant={WsTypoVariants.SUB_TITLE}
          bold
          color={WsTypoColors.PRIMARY}
          cursor="pointer"
        >
          {params.row['templateName']}
        </WsTypography>
      ),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      flex: 0.5,
    },
    {
      field: 'createdOn',
      headerName: 'Created On',
      flex: 0.5,
      renderCell: (params) => <>{getDateUsingFormat(params.row['createdOn'], 'DD-MMM-YY')}</>,
    },
    {
      field: 'Action',
      headerName: '',
      flex: 0.5,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleEditItem(params.row['index'] as number)}>
            <Edit color="primary" />
          </IconButton>
          <IconButton onClick={() => handleDeleteItem(params.row['index'] as string)}>
            <Delete color="primary" />
          </IconButton>
        </>
      ),
    },
  ];

  const createRTREmailTemplate = () => {
    setIsEditRtrTemplate(false);
    setShowCreateTemplate(true);
  };

  const handleEditItem = async (id: number) => {
    setIsEditRtrTemplate(true);
    setShowCreateTemplate(true);
    setRtrListRecord(rtrLisrtData[id]);
  };
  const handleDeleteItem = async (data: any) => {
    setOpenDeleteDialog(true);
    setSelectedItem(rtrLisrtData[data]);
  };
  const deleteSurvey = async () => {
    try {
      await deleteAllRtrTemplate(selectedItem?.id);
      getAllRtrTemplateList();
    } catch (error) {
      console.error('error', error);
    } finally {
      setOpenDeleteDialog(false);
    }
  };
  const handleCloseDialog = () => setOpenDeleteDialog(false);

  return (
    <>
      {showCreateTemplate ? (
        <Box>
          <WsDrawer
            size="full"
            open={true}
            onClose={() => closeDialog()}
            iconType="arrow"
            title="Back Template List"
          >
            <CreateTemplateEditor
              onSaveTemplate={() => {}}
              onEditOrCloneTemplate={onEditOrCloneTemplate}
              variant={variant}
              type={templateType}
              isRTRTemplate={true}
              isRTRTemplateEdit={isEditRtrTemplate}
              dataRTRTemplate={rtrLisrtRecord}
            />
          </WsDrawer>
        </Box>
      ) : (
        <SettingsPageLayout
          title={'RTR Template'}
          indexOptions={[{ label: 'RTR Template', url: '/rtr-template' }]}
          breadCrumnbsList={getSettingBreadCrumbList(
            ISettingBreadCrumbTypes.CRM,
            Pages['RTR_TEMPLATE']
          )}
        >
          <Grid sx={{ backgroundColor: WsColors.TheBlues50 }} container py={1}>
            <ConfirmDeleteDialog
              open={openDeleteDialog}
              onClose={handleCloseDialog}
              onConfirm={deleteSurvey}
              deletingItem={`${selectedItem?.templateName ?? ''} templateName`}
            />
            <Grid item xs={12} justifyContent="flex-end" pt={2}>
              <FormProvider {...methods}>
                <Grid container sx={{ display: 'flex' }} component="form"></Grid>
              </FormProvider>
            </Grid>
            <Grid item xs={12} sx={{ ml: 8 }} container justifyContent="flex-end">
              <Grid item xs={3} p={1} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <WsButton
                  fullWidth
                  onClick={() => createRTREmailTemplate()}
                  variant="contained"
                  size="large"
                  className={classes.customButton}
                >
                  Create RTR Template
                </WsButton>
              </Grid>
            </Grid>
            <Grid xs={12} sx={{ backgroundColor: 'white' }}>
              <DataGrid
                autoHeight={true}
                columns={columns}
                rows={tableData}
                onPageChange={(page) => setPageNo(page)}
                page={pageNo}
                rowCount={totalRecord}
                pageSize={pageSize}
                rowsPerPageOptions={[25, 50, 75, 100]}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                onSortModelChange={(newSortModel) => {
                  setPageNo(0);
                  setSortModel(newSortModel);
                }}
                sortingMode="server"
                paginationMode="server"
                getRowHeight={() => 'auto'}
                sx={{
                  [`& .${gridClasses.cell}`]: {
                    py: 1,
                  },
                }}
              />
            </Grid>
          </Grid>
        </SettingsPageLayout>
      )}
    </>
  );
};
export default RTRTemplate;
